import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography,
} from '@mui/material';
import { pct, px } from '../utils/dynamanSimulation';
import { pointsOf, riskPointsOf } from '../utils/portfolioMetrics';
import { entryColour } from '../utils/replayAnnotations';

/**
 * The trade ledger, with a synthesised narrative under every row.
 *
 * <h2>The open position is a SEPARATE table, not the last row of this one</h2>
 *
 * It has not produced a result — its number is a mark. Putting it at the bottom of the same table
 * invites exactly the reading the statistics panel is careful to avoid, because a reader scanning a
 * PnL column has no way to tell that the last figure is of a different kind. On this rule set the
 * difference is not subtle: the SPY run's open mark is over 1,500% against realised trades of
 * around 1%.
 *
 * <h2>The narrative is rendered as its own row</h2>
 *
 * Not a tooltip. It is the thing the feature was asked for — a summary you read while tuning — and
 * a tooltip is for something you look up, not something you scan. It is also generated server-side
 * from the record's own fields, so what is shown here is exactly what the CSV export carries.
 */

/**
 * Columns, each with its own id.
 *
 * Keyed on an id rather than on the label because "TF" appears twice — once for the entry series and
 * once for the exit. Keyed on the label, React sees two children with the same key and is entitled
 * to duplicate or omit one of them.
 */
const HEADERS = [
  { id: 'seq', label: '#' },
  { id: 'dir', label: 'Dir' },
  // THE RULE THAT OPENED IT. For PTA/PTB the five-letter key -- ARUT-B, APDT-S -- which is the
  // only column that answers "which of the eight rules claimed this bounce" without reading the
  // detail prose. Blank for a breakout entry, which no provider labels.
  { id: 'signal', label: 'Signal' },
  // HOW OLD THE BAND LINE WAS when the signal fired, in calendar days since the band closed.
  //
  // It is a column because it was the subject of a measurement rather than a curiosity: the
  // stale-line hypothesis -- that a simultaneous reversal is only toxic on an antique level -- was
  // tested on the 95 admitted PTA trades and refuted BACKWARDS. The freshest lines were the
  // catastrophic ones (0-10 days: eight trades, zero winners, PF 0.00) and the only
  // above-break-even bucket was 90-150 days. A reader auditing the chart should be able to sort by
  // this and see that for themselves.
  //
  // Blank for a non-band run, which has no lines at all.
  { id: 'lineAge', label: 'Line age', align: 'right' },
  { id: 'entry', label: 'Entry' },
  { id: 'entryTf', label: 'TF' },
  { id: 'entryPx', label: 'Entry px', align: 'right' },
  { id: 'stop', label: 'Stop', align: 'right' },
  { id: 'exit', label: 'Exit' },
  { id: 'exitTf', label: 'TF' },
  { id: 'exitPx', label: 'Exit px', align: 'right' },
  { id: 'reason', label: 'Exit reason' },
  { id: 'bars', label: 'Bars', align: 'right' },
  { id: 'days', label: 'Days', align: 'right' },
  { id: 'mfe', label: 'MFE', align: 'right' },
  { id: 'pnl', label: 'PnL', align: 'right' },
  // POINTS AND THE RISK THEY WERE TAKEN AGAINST, beside the R they divide into. R alone hides
  // the SCALE -- whether a 3R winner was 3 points against a tight node stop or 60 against a wide
  // one -- which is the whole question a fixed-point stop raises.
  { id: 'points', label: 'Points', align: 'right' },
  { id: 'riskPts', label: 'Risk pts', align: 'right' },
  { id: 'r', label: 'R', align: 'right' },
];

const HeaderCells = () => HEADERS.map((h) => (
  <TableCell
    key={h.id}
    align={h.align || 'left'}
    sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
  >
    {h.label}
  </TableCell>
));

const stamp = (iso) => (iso ? String(iso).replace('T', ' ').slice(0, 16) : '—');

const TradeRows = ({ trades, showNarratives }) =>
  trades.map((t) => (
    <React.Fragment key={`${t.sequence}-${t.entryTime}`}>
      <TableRow hover sx={{ '& td': { borderBottom: showNarratives ? 'none' : undefined } }}>
        <TableCell>{t.sequence}</TableCell>
        <TableCell>{t.direction}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {/* A RELOAD rides BESIDE the signal chip rather than replacing it, because the two
              answer different questions: the code says which rule claimed the bounce, and this
              says the premise was the second leg of a wave the accelerating fan had already cut.
              Replacing the code would lose the rule; omitting the flag would make a reload
              indistinguishable from a fresh structural entry, which is the one thing the
              mechanic was asked to be distinguishable from. */}
          {t.reload
            ? (
              <Tooltip
                arrow
                title="RELOAD — the fan ended the previous wave, price retraced into its own
                       38.2–61.8% band and turned, and the engine re-entered."
              >
                <Chip
                  size="small"
                  label="RELOAD"
                  variant="filled"
                  color="warning"
                  sx={{ fontWeight: 700, height: 20, mr: 0.5 }}
                />
              </Tooltip>
            )
            : null}
          {/* A CONTINUATION rides beside the code for the same reason a reload does, and needs it
              more: it carries NO signal code at all -- it is not a rule firing, it is a level
              being crossed back through -- so without this chip the row is simply blank in the
              one column a reader uses to tell entries apart. */}
          {t.continuation
            ? (
              <Tooltip
                arrow
                title="CONTINUATION — an earlier premise was stopped or fanned out, and price has
                       crossed back through the price it was booked out at with the trend
                       agreeing."
              >
                <Chip
                  size="small"
                  label="CONT"
                  variant="filled"
                  color="info"
                  sx={{ fontWeight: 700, height: 20, mr: 0.5 }}
                />
              </Tooltip>
            )
            : null}
          {t.entrySignal
            ? (
              <Chip
                size="small"
                label={t.entrySignal}
                variant="outlined"
                // COLOURED BY BREACH TYPE, matching the chart's entry verticals exactly.
                //
                // The three containment breaches are not one population -- A-ESC-B is 135 trades
                // at PF 1.35 while A-RUN-B is 12 at PF 0.15 -- so sorting a 159-row ledger by eye
                // needs them visually separable, not merely spelled differently. The colour comes
                // from the same function the chart uses, so a row and its vertical can never
                // disagree about which breach a trade was.
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  height: 20,
                  color: entryColour(t.entrySignal, t.direction),
                  borderColor: entryColour(t.entrySignal, t.direction),
                }}
              />
            )
            : <span style={{ opacity: 0.4 }}>—</span>}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap', opacity: 0.85 }}>
          {Number.isFinite(t.lineAgeDays) ? `${t.lineAgeDays}d` : ''}
        </TableCell>
        <TableCell>{stamp(t.entryTime)}</TableCell>
        <TableCell>{t.entryTimeframe}</TableCell>
        <TableCell align="right">{px(t.entryPrice)}</TableCell>
        <TableCell align="right">{px(t.initialStop)}</TableCell>
        <TableCell>{stamp(t.exitTime)}</TableCell>
        <TableCell>
          {/* The exit timeframe is the point of the intraday bailout: a 65m exit dated 10:35 is
              the rule doing what it exists to do, hours before the daily bar. */}
          <Chip
            size="small"
            label={t.exitTimeframe}
            color={t.exitTimeframe !== t.entryTimeframe ? 'secondary' : 'default'}
            variant="outlined"
          />
        </TableCell>
        <TableCell align="right">{px(t.exitPrice)}</TableCell>
        <TableCell>
          <Tooltip title={t.detail || ''} arrow>
            <span>{t.exitReason}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">{t.barsHeld}</TableCell>
        <TableCell align="right">{t.tradingDays}</TableCell>
        <TableCell align="right">
          <Tooltip
            title={`Best price reached: ${px(t.maxFavourableExcursion)}, exited at ${
              px(t.exitPrice)} — ${t.giveBackPercent.toFixed(2)} percentage points of return handed `
              + 'back. (Points of return, not a price fall: the two differ by a lot on a long hold.)'}
            arrow
          >
            <span>{pct(t.maxFavourableExcursionPercent)}</span>
          </Tooltip>
        </TableCell>
        <TableCell
          align="right"
          sx={{ color: t.pnlPercent > 0 ? 'success.main' : t.pnlPercent < 0 ? 'error.main' : undefined,
            fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
        >
          {pct(t.pnlPercent)}
        </TableCell>
        <TableCell
          align="right"
          sx={{ color: pointsOf(t) > 0 ? 'success.main' : pointsOf(t) < 0 ? 'error.main' : undefined,
            fontVariantNumeric: 'tabular-nums' }}
        >
          {`${pointsOf(t) >= 0 ? '+' : ''}${pointsOf(t).toFixed(2)}`}
        </TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {riskPointsOf(t).toFixed(2)}
        </TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {t.rMultiple.toFixed(2)}
        </TableCell>
      </TableRow>

      {showNarratives && (
        <TableRow>
          <TableCell />
          <TableCell colSpan={HEADERS.length - 1} sx={{ pt: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t.narrative}
            </Typography>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  ));

const DynaManLedgerTable = ({ completedTrades, openTrade = null, showNarratives = true }) => (
  <Box>
    <Typography variant="subtitle2" gutterBottom>
      Completed trades ({completedTrades.length})
    </Typography>

    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 460 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow><HeaderCells /></TableRow>
        </TableHead>
        <TableBody>
          {completedTrades.length === 0 ? (
            <TableRow>
              <TableCell colSpan={HEADERS.length}>
                <Typography variant="body2" color="text.secondary">
                  No trade completed in this run.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            <TradeRows trades={completedTrades} showNarratives={showNarratives} />
          )}
        </TableBody>
      </Table>
    </TableContainer>

    <Typography variant="subtitle2" gutterBottom>Open position at the end of the data</Typography>
    {openTrade ? (
      <>
        <Alert severity="info" sx={{ mb: 1 }}>
          Unrealised — marked to the last close, not a result. Excluded from every statistic above.
        </Alert>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow><HeaderCells /></TableRow>
            </TableHead>
            <TableBody>
              <TradeRows trades={[openTrade]} showNarratives={showNarratives} />
            </TableBody>
          </Table>
        </TableContainer>
      </>
    ) : (
      <Typography variant="body2" color="text.secondary">Flat — nothing held.</Typography>
    )}
  </Box>
);

const tradeShape = PropTypes.shape({
  sequence: PropTypes.number,
  direction: PropTypes.string,
  entryTime: PropTypes.string,
  entryTimeframe: PropTypes.string,
  entryPrice: PropTypes.number,
  initialStop: PropTypes.number,
  exitTime: PropTypes.string,
  exitTimeframe: PropTypes.string,
  exitPrice: PropTypes.number,
  exitReason: PropTypes.string,
  reload: PropTypes.bool,
  continuation: PropTypes.bool,
  detail: PropTypes.string,
  barsHeld: PropTypes.number,
  tradingDays: PropTypes.number,
  pnlPercent: PropTypes.number,
  rMultiple: PropTypes.number,
  maxFavourableExcursion: PropTypes.number,
  maxFavourableExcursionPercent: PropTypes.number,
  giveBackPercent: PropTypes.number,
  narrative: PropTypes.string,
});

TradeRows.propTypes = {
  trades: PropTypes.arrayOf(tradeShape).isRequired,
  showNarratives: PropTypes.bool,
};

DynaManLedgerTable.propTypes = {
  completedTrades: PropTypes.arrayOf(tradeShape).isRequired,
  openTrade: tradeShape,
  showNarratives: PropTypes.bool,
};

export default DynaManLedgerTable;
